import { colIndex, colName, normalizeRange, parseRange, rangeToA1, ref } from './a1'
import { createEvaluator } from './engine'
import { getEvaluator, useStore, usedRange } from './store'
import type { CellRange, CellStyle, Sheet } from './types'

/** OpenAI/OpenRouter function-calling 스키마 */
export interface ToolDef {
  type: 'function'
  function: { name: string; description: string; parameters: Record<string, any> }
}

const S = (description: string) => ({ type: 'string', description })
const N = (description: string) => ({ type: 'number', description })
const B = (description: string) => ({ type: 'boolean', description })
const SHEET = S('대상 시트 이름. 생략하면 현재 활성 시트.')

function def(
  name: string,
  description: string,
  properties: Record<string, any>,
  required: string[] = []
): ToolDef {
  return {
    type: 'function',
    function: {
      name,
      description,
      parameters: { type: 'object', properties, required, additionalProperties: false },
    },
  }
}

export const TOOLS: ToolDef[] = [
  def('list_sheets', '모든 시트의 이름·크기·사용범위와 현재 선택 영역을 반환한다.', {}),

  def(
    'read_range',
    '지정 범위의 계산된 값을 2차원 배열로 읽는다. 한 번에 최대 5000셀. 데이터를 보기 전에 항상 먼저 호출할 것.',
    {
      sheet: SHEET,
      range: S('A1 표기 범위. 예: "A1:F20", "B:B", "A5". 생략하면 시트의 사용범위 전체.'),
      include_formulas: B('true 면 수식 원문도 함께 반환한다. 기본 false.'),
    }
  ),

  def(
    'write_range',
    '왼쪽 위 시작 셀부터 2차원 배열을 한 번에 기록한다. 여러 셀을 쓸 때는 write_cells 보다 이 도구를 쓸 것. "=" 로 시작하면 수식으로 저장된다. 빈 문자열은 셀을 비운다.',
    {
      sheet: SHEET,
      start: S('시작 셀. 예: "A1"'),
      values: {
        type: 'array',
        description: '행 배열의 배열. 모든 값은 문자열로 전달.',
        items: { type: 'array', items: { type: 'string' } },
      },
    },
    ['start', 'values']
  ),

  def(
    'write_cells',
    '흩어진 개별 셀들을 기록한다. "=" 로 시작하면 수식.',
    {
      sheet: SHEET,
      cells: {
        type: 'array',
        items: {
          type: 'object',
          properties: { ref: S('셀 주소. 예: "C7"'), value: S('값 또는 수식') },
          required: ['ref', 'value'],
        },
      },
    },
    ['cells']
  ),

  def('clear_range', '범위의 내용/서식을 지운다.', {
    sheet: SHEET,
    range: S('A1 표기 범위'),
    what: { type: 'string', enum: ['all', 'contents', 'formats'], description: '기본 contents' },
  }, ['range']),

  def(
    'format_range',
    '범위에 서식을 적용한다. 지정하지 않은 속성은 그대로 유지되고, 빈 문자열("")을 주면 해당 속성이 제거된다.',
    {
      sheet: SHEET,
      range: S('A1 표기 범위'),
      bold: B('굵게'),
      italic: B('기울임'),
      underline: B('밑줄'),
      color: S('글자색 CSS hex. 예: "#c00000"'),
      bg: S('배경색 CSS hex. 예: "#fff2cc"'),
      align: { type: 'string', enum: ['left', 'center', 'right'] },
      vertical_align: { type: 'string', enum: ['top', 'middle', 'bottom'] },
      wrap: B('셀 안에서 자동 줄바꿈'),
      num_format: S('Excel 표시형식. 예: "#,##0", "0.0%", "₩#,##0", "yyyy-mm-dd"'),
      font_size: N('글자 크기(px). 기본 13'),
      font_family: S('글꼴 이름. 예: "맑은 고딕", "Arial"'),
      indent: N('들여쓰기 단계. 0~15'),
      text_rotation: N('텍스트 회전 각도. -90~90'),
      border_style: { type: 'string', enum: ['none', 'thin', 'medium', 'thick', 'dashed', 'dotted', 'double'] },
      border_color: S('테두리색 CSS hex. 예: "#000000"'),
      border_sides: {
        type: 'array',
        items: { type: 'string', enum: ['top', 'right', 'bottom', 'left'] },
        description: '적용할 변. 생략하면 네 변 전체',
      },
    },
    ['range']
  ),

  def('merge_cells', '범위를 하나의 셀로 병합한다. 좌상단 값만 유지한다.', {
    sheet: SHEET,
    range: S('A1 표기 범위. 예: A1:C2'),
    center: B('병합 후 가로/세로 가운데 맞춤. 기본 true'),
  }, ['range']),

  def('unmerge_cells', '범위와 겹치는 병합 셀을 해제한다.', {
    sheet: SHEET,
    range: S('A1 표기 범위'),
  }, ['range']),

  def('insert_rows', '지정 행 위치에 행을 삽입한다(수식 참조 자동 보정).', {
    sheet: SHEET,
    at: N('삽입될 첫 행 번호(1-based)'),
    count: N('삽입 개수. 기본 1'),
  }, ['at']),

  def('delete_rows', '행을 삭제한다(수식 참조 자동 보정).', {
    sheet: SHEET,
    at: N('삭제 시작 행 번호(1-based)'),
    count: N('삭제 개수. 기본 1'),
  }, ['at']),

  def('insert_columns', '열을 삽입한다.', {
    sheet: SHEET,
    at: S('삽입될 첫 열 문자. 예: "C"'),
    count: N('삽입 개수. 기본 1'),
  }, ['at']),

  def('delete_columns', '열을 삭제한다.', {
    sheet: SHEET,
    at: S('삭제 시작 열 문자. 예: "C"'),
    count: N('삭제 개수. 기본 1'),
  }, ['at']),

  def(
    'sort_range',
    '범위를 특정 열 기준으로 정렬한다. 헤더가 있으면 has_header=true.',
    {
      sheet: SHEET,
      range: S('정렬할 전체 범위. 예: "A1:E50"'),
      by_column: S('정렬 기준 열 문자. 예: "C"'),
      order: { type: 'string', enum: ['asc', 'desc'], description: '기본 asc' },
      has_header: B('첫 행이 머리글이면 true'),
    },
    ['range', 'by_column']
  ),

  def('find_replace', '문자열을 찾아 바꾼다. 바뀐 셀 수를 반환.', {
    sheet: SHEET,
    find: S('찾을 문자열'),
    replace: S('바꿀 문자열'),
    match_case: B('대소문자 구분. 기본 false'),
    all_sheets: B('모든 시트 대상. 기본 false'),
  }, ['find', 'replace']),

  def('add_sheet', '새 시트를 추가하고 활성화한다.', { name: S('시트 이름') }),
  def('rename_sheet', '시트 이름을 바꾼다(다른 수식의 참조도 함께 변경).', {
    sheet: SHEET,
    new_name: S('새 이름'),
  }, ['new_name']),
  def('delete_sheet', '시트를 삭제한다.', { sheet: SHEET }),
  def('activate_sheet', '지정 시트를 화면에 띄운다.', { sheet: SHEET }, ['sheet']),

  def('set_column_width', '열 너비를 px 로 지정한다.', {
    sheet: SHEET,
    columns: S('열 또는 열 범위. 예: "A" 또는 "A:D"'),
    width: N('px'),
  }, ['columns', 'width']),

  def('select_range', '사용자 화면의 선택 영역을 옮긴다(결과를 가리킬 때 사용).', {
    sheet: SHEET,
    range: S('A1 표기 범위'),
  }, ['range']),

  def(
    'export_pdf',
    'PDF 만들기 대화상자를 옵션이 채워진 채로 연다. 사용자가 확인하면 브라우저 인쇄 대화상자로 넘어간다. 실제 저장은 사용자가 누른다.',
    {
      scope: { type: 'string', enum: ['sheet', 'selection', 'workbook'], description: '기본 sheet' },
      paper: { type: 'string', enum: ['A4', 'A3', 'B5', 'Letter', 'Legal'] },
      orientation: { type: 'string', enum: ['portrait', 'landscape'] },
      fit: {
        type: 'string',
        enum: ['none', 'width', 'page'],
        description: 'width=너비 맞춤(기본), page=한 장에 전부, none=배율 직접',
      },
      scale: N('fit=none 일 때 배율(%)'),
      gridlines: B('격자선 인쇄'),
      repeat_rows: N('페이지마다 반복할 상단 행 수'),
    }
  ),

  def(
    'run_parallel',
    [
      '서로 독립적인 하위 작업 여러 개를 동시에 실행한다. 각 작업은 같은 도구를 쓸 수 있는 별도 에이전트가 맡는다.',
      '시트가 여러 개거나, 열/블록별로 같은 처리를 반복해야 하거나, 조사와 작성이 동시에 가능할 때 반드시 이걸 쓴다.',
      '⚠ 각 작업의 대상 범위가 서로 겹치지 않게 지시문에 명시할 것 — 겹치면 서로 덮어쓴다.',
      '각 instruction 은 그 자체로 완결되어야 한다(대화 맥락을 물려받지 않음).',
    ].join(' '),
    {
      tasks: {
        type: 'array',
        description: '동시에 돌릴 작업 목록 (2~8개)',
        items: {
          type: 'object',
          properties: {
            label: S('진행상황에 표시할 짧은 이름. 예: "Sheet2 요약"'),
            instruction: S('그 작업만 보고도 수행 가능한 완결된 지시문. 대상 시트·범위를 반드시 포함.'),
          },
          required: ['label', 'instruction'],
        },
      },
      max_concurrency: N('동시 실행 수. 기본 5, 최대 8'),
    },
    ['tasks']
  ),
]

/** 읽기 전용 도구 — 한 번에 여러 개가 오면 동시에 실행해도 안전하다. */
export const READ_ONLY_TOOLS = new Set(['list_sheets', 'read_range'])

// ── 실행기 ───────────────────────────────────────────────────────────────────

const MAX_READ_CELLS = 5000

/**
 * 시트 이름 → Sheet.
 * 이름이 틀렸을 때 조용히 활성 시트로 폴백하면 병렬 레인이 엉뚱한 시트를 덮어쓴다.
 * 생략은 허용하되, 틀린 이름은 반드시 에러로 되돌려준다.
 */
function resolveSheet(name?: string): Sheet {
  const st = useStore.getState()
  if (name === undefined || name === null || String(name).trim() === '') return st.activeSheet()
  const key = String(name).trim().toLowerCase()
  const found = st.wb.sheets.find((s) => s.name.toLowerCase() === key)
  if (!found)
    throw new Error(
      `"${name}" 시트가 없습니다. 현재 시트: ${st.wb.sheets.map((s) => s.name).join(', ')}`
    )
  return found
}

function need(v: any, msg: string) {
  if (v === undefined || v === null || v === '') throw new Error(msg)
  return v
}

function rangeOf(sheet: Sheet, rangeStr?: string): CellRange {
  if (!rangeStr) {
    const u = usedRange(sheet)
    return u ?? { r1: 1, c1: 1, r2: 1, c2: 1 }
  }
  const parsed = parseRange(rangeStr, sheet.rows, sheet.cols)
  if (!parsed) throw new Error(`범위를 해석할 수 없습니다: "${rangeStr}"`)
  return normalizeRange(parsed.range)
}

function touchedKeys(sheet: Sheet, r: CellRange, cap = 400): string[] {
  const out: string[] = []
  const n = normalizeRange(r)
  for (let row = n.r1; row <= n.r2 && out.length < cap; row++)
    for (let col = n.c1; col <= n.c2 && out.length < cap; col++) out.push(`${sheet.id}!${ref(row, col)}`)
  return out
}

export interface ToolOutcome {
  ok: boolean
  [k: string]: any
}

/** AI 한 턴(=하나의 undo 묶음)을 식별하는 그룹 키 */
export function newTurnGroup() {
  return `ai:${Date.now().toString(36)}:${Math.random().toString(36).slice(2, 6)}`
}

// ── 병렬 하위 에이전트 ───────────────────────────────────────────────────────

export interface LaneEvent {
  id: number
  label: string
  status: 'running' | 'done' | 'failed'
  detail?: string
  toolCount?: number
}

export interface SubTask {
  label: string
  instruction: string
}

export interface ToolCtx {
  group: string
  model?: string
  onLane?: (e: LaneEvent) => void
}

export type SubAgentRunner = (
  task: SubTask,
  ctx: ToolCtx & { laneId: number }
) => Promise<{ ok: boolean; summary: string; tools: string[] }>

let subAgentRunner: SubAgentRunner | null = null

/** App 이 PDF 대화상자 오프너를 여기에 꽂아준다 */
let pdfOpener: ((patch: Record<string, any>) => void) | null = null
export function setPdfOpener(fn: (patch: Record<string, any>) => void) {
  pdfOpener = fn
}

/** agent.ts 가 자기 루프를 여기에 꽂아준다 (tools ↔ agent 순환 import 회피) */
export function setSubAgentRunner(fn: SubAgentRunner) {
  subAgentRunner = fn
}

/** 동시 실행 수를 제한하는 워커 풀 */
async function pool<T, R>(items: T[], limit: number, fn: (item: T, i: number) => Promise<R>): Promise<R[]> {
  const out = new Array<R>(items.length)
  let cursor = 0
  const workers = Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, async () => {
    for (;;) {
      const i = cursor++
      if (i >= items.length) return
      out[i] = await fn(items[i], i)
    }
  })
  await Promise.all(workers)
  return out
}

export async function executeTool(name: string, args: any, ctx: ToolCtx): Promise<ToolOutcome> {
  const st = useStore.getState()
  const a = args ?? {}
  const group = ctx.group

  if (name === 'run_parallel') {
    if (!subAgentRunner) throw new Error('병렬 실행기가 초기화되지 않았습니다.')
    const tasks: SubTask[] = Array.isArray(a.tasks) ? a.tasks : []
    if (tasks.length < 2) throw new Error('run_parallel 은 작업이 2개 이상일 때만 씁니다. 그냥 직접 실행하세요.')
    if (tasks.length > 8) throw new Error('한 번에 최대 8개까지만 병렬 실행할 수 있습니다.')
    const limit = Math.min(8, Math.max(1, Number(a.max_concurrency ?? 5)))
    const started = performance.now()

    const results = await pool(tasks, limit, async (task, i) => {
      const laneId = i + 1
      ctx.onLane?.({ id: laneId, label: task.label, status: 'running' })
      try {
        const r = await subAgentRunner!(task, { ...ctx, laneId })
        ctx.onLane?.({
          id: laneId,
          label: task.label,
          status: r.ok ? 'done' : 'failed',
          detail: r.summary,
          toolCount: r.tools.length,
        })
        return { label: task.label, ok: r.ok, result: r.summary, tools_used: r.tools }
      } catch (e: any) {
        const msg = e?.message ?? String(e)
        ctx.onLane?.({ id: laneId, label: task.label, status: 'failed', detail: msg })
        return { label: task.label, ok: false, result: msg, tools_used: [] }
      }
    })

    return {
      ok: results.every((r) => r.ok),
      lanes: results.length,
      concurrency: limit,
      elapsed_ms: Math.round(performance.now() - started),
      results,
    }
  }

  switch (name) {
    case 'list_sheets': {
      const sel = st.selRange()
      return {
        ok: true,
        active_sheet: st.activeSheet().name,
        selection: `${st.activeSheet().name}!${rangeToA1(sel)}`,
        sheets: st.wb.sheets.map((s) => {
          const u = usedRange(s)
          return {
            name: s.name,
            grid: `${s.rows}행 × ${colName(s.cols)}열`,
            used_range: u ? rangeToA1(u) : null,
            cell_count: Object.keys(s.cells).length,
          }
        }),
      }
    }

    case 'read_range': {
      const sheet = resolveSheet(a.sheet)
      const r = rangeOf(sheet, a.range)
      const cells = (r.r2 - r.r1 + 1) * (r.c2 - r.c1 + 1)
      if (cells > MAX_READ_CELLS)
        throw new Error(`범위가 너무 큽니다(${cells}셀). ${MAX_READ_CELLS}셀 이하로 나눠서 읽으세요.`)
      const ev = createEvaluator(st.wb.sheets, sheet.name)
      const values: string[][] = []
      const formulas: Record<string, string> = {}
      for (let row = r.r1; row <= r.r2; row++) {
        const line: string[] = []
        for (let col = r.c1; col <= r.c2; col++) {
          line.push(ev.display(sheet.name, row, col).text)
          const f = sheet.cells[ref(row, col)]?.f
          if (a.include_formulas && f) formulas[ref(row, col)] = f
        }
        values.push(line)
      }
      return {
        ok: true,
        sheet: sheet.name,
        range: rangeToA1(r),
        columns: Array.from({ length: r.c2 - r.c1 + 1 }, (_, i) => colName(r.c1 + i)),
        first_row: r.r1,
        values,
        ...(a.include_formulas ? { formulas } : {}),
      }
    }

    case 'write_range': {
      const sheet = resolveSheet(a.sheet)
      const start = parseRange(need(a.start, 'start 가 필요합니다.'), sheet.rows, sheet.cols)
      if (!start) throw new Error(`start 를 해석할 수 없습니다: ${a.start}`)
      const values: any[][] = need(a.values, 'values 가 필요합니다.')
      if (!Array.isArray(values) || !Array.isArray(values[0]))
        throw new Error('values 는 2차원 배열이어야 합니다.')
      const r0 = start.range.r1
      const c0 = start.range.c1
      const entries: Array<{ row: number; col: number; input: string }> = []
      values.forEach((line, i) => {
        ;(Array.isArray(line) ? line : [line]).forEach((v, j) => {
          entries.push({ row: r0 + i, col: c0 + j, input: v === null || v === undefined ? '' : String(v) })
        })
      })
      if (entries.length > 20000) throw new Error('한 번에 20000셀을 넘길 수 없습니다.')
      st.setCellsRaw(sheet.id, entries, 'AI 입력', group)
      const rng: CellRange = {
        r1: r0,
        c1: c0,
        r2: r0 + values.length - 1,
        c2: c0 + Math.max(...values.map((l) => (Array.isArray(l) ? l.length : 1))) - 1,
      }
      st.markAi(touchedKeys(sheet, rng))
      return { ok: true, sheet: sheet.name, written: rangeToA1(rng), cells: entries.length }
    }

    case 'write_cells': {
      const sheet = resolveSheet(a.sheet)
      const list: Array<{ ref: string; value: any }> = need(a.cells, 'cells 가 필요합니다.')
      const entries = list.map((c) => {
        const p = parseRange(String(c.ref), sheet.rows, sheet.cols)
        if (!p) throw new Error(`셀 주소를 해석할 수 없습니다: ${c.ref}`)
        return {
          row: p.range.r1,
          col: p.range.c1,
          input: c.value === null || c.value === undefined ? '' : String(c.value),
        }
      })
      st.setCellsRaw(sheet.id, entries, 'AI 입력', group)
      st.markAi(entries.map((e) => `${sheet.id}!${ref(e.row, e.col)}`))
      return { ok: true, sheet: sheet.name, cells: entries.length }
    }

    case 'clear_range': {
      const sheet = resolveSheet(a.sheet)
      const r = rangeOf(sheet, need(a.range, 'range 가 필요합니다.'))
      st.clearRange(sheet.id, r, a.what ?? 'contents', group)
      st.markAi(touchedKeys(sheet, r))
      return { ok: true, sheet: sheet.name, cleared: rangeToA1(r) }
    }

    case 'format_range': {
      const sheet = resolveSheet(a.sheet)
      const r = rangeOf(sheet, need(a.range, 'range 가 필요합니다.'))
      const patch: CellStyle = {}
      if (a.bold !== undefined) patch.bold = a.bold
      if (a.italic !== undefined) patch.italic = a.italic
      if (a.underline !== undefined) patch.underline = a.underline
      if (a.color !== undefined) patch.color = a.color
      if (a.bg !== undefined) patch.bg = a.bg
      if (a.align !== undefined) patch.align = a.align
      if (a.vertical_align !== undefined) patch.verticalAlign = a.vertical_align
      if (a.wrap !== undefined) patch.wrap = a.wrap
      if (a.num_format !== undefined) patch.numFmt = a.num_format
      if (a.font_size !== undefined) patch.fontSize = a.font_size
      if (a.font_family !== undefined) patch.fontFamily = a.font_family
      if (a.indent !== undefined) patch.indent = Math.max(0, Math.min(15, Number(a.indent)))
      if (a.text_rotation !== undefined) patch.textRotation = Math.max(-90, Math.min(90, Number(a.text_rotation)))
      if (a.border_style !== undefined || a.border_color !== undefined || a.border_sides !== undefined) {
        const sides = Array.isArray(a.border_sides) && a.border_sides.length
          ? a.border_sides : ['top', 'right', 'bottom', 'left']
        const border = a.border_style === 'none' ? undefined : {
          style: a.border_style ?? 'thin',
          color: a.border_color ?? '#000000',
        }
        for (const side of sides) {
          if (['top', 'right', 'bottom', 'left'].includes(side))
            (patch as any)[`border${side[0].toUpperCase()}${side.slice(1)}`] = border
        }
      }
      if (!Object.keys(patch).length) throw new Error('적용할 서식 속성이 하나도 없습니다.')
      st.formatRange(sheet.id, r, patch, group)
      st.markAi(touchedKeys(sheet, r))
      return { ok: true, sheet: sheet.name, formatted: rangeToA1(r), applied: patch }
    }

    case 'merge_cells': {
      const sheet = resolveSheet(a.sheet)
      const r = rangeOf(sheet, need(a.range, 'range 가 필요합니다.'))
      if (a.center !== false) st.formatRange(sheet.id, r, { align: 'center', verticalAlign: 'middle' }, group)
      st.mergeRange(sheet.id, r, group)
      st.markAi(touchedKeys(sheet, r))
      return { ok: true, sheet: sheet.name, merged: rangeToA1(r) }
    }

    case 'unmerge_cells': {
      const sheet = resolveSheet(a.sheet)
      const r = rangeOf(sheet, need(a.range, 'range 가 필요합니다.'))
      st.unmergeRange(sheet.id, r, group)
      return { ok: true, sheet: sheet.name, unmerged: rangeToA1(r) }
    }

    case 'insert_rows': {
      const sheet = resolveSheet(a.sheet)
      st.insertRows(sheet.id, Math.max(1, Number(need(a.at, 'at 이 필요합니다.'))), Math.max(1, Number(a.count ?? 1)), group)
      return { ok: true, sheet: sheet.name, inserted_at_row: Number(a.at), count: Number(a.count ?? 1) }
    }
    case 'delete_rows': {
      const sheet = resolveSheet(a.sheet)
      st.deleteRows(sheet.id, Math.max(1, Number(need(a.at, 'at 이 필요합니다.'))), Math.max(1, Number(a.count ?? 1)), group)
      return { ok: true, sheet: sheet.name, deleted_from_row: Number(a.at), count: Number(a.count ?? 1) }
    }
    case 'insert_columns': {
      const sheet = resolveSheet(a.sheet)
      const at = colIndex(String(need(a.at, 'at 이 필요합니다.')).replace(/[^A-Za-z]/g, '') || 'A')
      st.insertCols(sheet.id, at, Math.max(1, Number(a.count ?? 1)), group)
      return { ok: true, sheet: sheet.name, inserted_at_col: colName(at), count: Number(a.count ?? 1) }
    }
    case 'delete_columns': {
      const sheet = resolveSheet(a.sheet)
      const at = colIndex(String(need(a.at, 'at 이 필요합니다.')).replace(/[^A-Za-z]/g, '') || 'A')
      st.deleteCols(sheet.id, at, Math.max(1, Number(a.count ?? 1)), group)
      return { ok: true, sheet: sheet.name, deleted_from_col: colName(at), count: Number(a.count ?? 1) }
    }

    case 'sort_range': {
      const sheet = resolveSheet(a.sheet)
      const r = rangeOf(sheet, need(a.range, 'range 가 필요합니다.'))
      const byRaw = String(need(a.by_column, 'by_column 이 필요합니다.')).trim()
      const byCol = /^[0-9]+$/.test(byRaw) ? r.c1 + Number(byRaw) - 1 : colIndex(byRaw.replace(/[^A-Za-z]/g, ''))
      if (byCol < r.c1 || byCol > r.c2) throw new Error(`정렬 기준 열 ${byRaw} 이 범위 밖입니다.`)
      st.sortRange(sheet.id, r, byCol, a.order === 'desc' ? 'desc' : 'asc', Boolean(a.has_header), group)
      st.markAi(touchedKeys(sheet, r))
      return { ok: true, sheet: sheet.name, sorted: rangeToA1(r), by: colName(byCol), order: a.order ?? 'asc' }
    }

    case 'find_replace': {
      const sheet = resolveSheet(a.sheet)
      const n = st.findReplace(
        a.all_sheets ? null : sheet.id,
        String(need(a.find, 'find 가 필요합니다.')),
        String(a.replace ?? ''),
        Boolean(a.match_case),
        group
      )
      return { ok: true, replaced_cells: n }
    }

    case 'add_sheet': {
      const s = st.addSheet(a.name, group)
      return { ok: true, created: s.name }
    }
    case 'rename_sheet': {
      const sheet = resolveSheet(a.sheet)
      const from = sheet.name
      st.renameSheet(sheet.id, String(need(a.new_name, 'new_name 이 필요합니다.')), group)
      return { ok: true, from, to: a.new_name }
    }
    case 'delete_sheet': {
      const sheet = resolveSheet(a.sheet)
      st.deleteSheet(sheet.id, group)
      return { ok: true, deleted: sheet.name }
    }
    case 'activate_sheet': {
      const sheet = resolveSheet(a.sheet)
      st.setActiveSheet(sheet.id)
      return { ok: true, active_sheet: sheet.name }
    }

    case 'set_column_width': {
      const sheet = resolveSheet(a.sheet)
      const spec = String(need(a.columns, 'columns 가 필요합니다.'))
      const [l, rr] = spec.split(':')
      const c1 = colIndex(l.replace(/[^A-Za-z]/g, ''))
      const c2 = rr ? colIndex(rr.replace(/[^A-Za-z]/g, '')) : c1
      const w = Math.max(24, Number(need(a.width, 'width 가 필요합니다.')))
      for (let c = Math.min(c1, c2); c <= Math.max(c1, c2); c++) st.setColWidth(sheet.id, c, w)
      return { ok: true, sheet: sheet.name, columns: spec, width: w }
    }

    case 'export_pdf': {
      if (!pdfOpener) throw new Error('PDF 기능이 아직 준비되지 않았습니다.')
      const patch: Record<string, any> = {}
      if (a.scope) patch.scope = a.scope
      if (a.paper) patch.paper = a.paper
      if (a.orientation) patch.orientation = a.orientation
      if (a.fit) patch.fit = a.fit
      if (a.scale !== undefined) patch.scale = Number(a.scale)
      if (a.gridlines !== undefined) patch.gridlines = Boolean(a.gridlines)
      if (a.repeat_rows !== undefined) patch.repeatRows = Math.max(0, Number(a.repeat_rows))
      pdfOpener(patch)
      return { ok: true, opened: 'PDF 대화상자', options: patch, note: '실제 저장은 사용자가 확인해야 합니다.' }
    }

    case 'select_range': {
      const sheet = resolveSheet(a.sheet)
      if (sheet.id !== st.wb.activeSheetId) st.setActiveSheet(sheet.id)
      const r = rangeOf(sheet, need(a.range, 'range 가 필요합니다.'))
      useStore.getState().select({ row: r.r1, col: r.c1 }, { row: r.r2, col: r.c2 })
      return { ok: true, selected: `${sheet.name}!${rangeToA1(r)}` }
    }

    default:
      throw new Error(`알 수 없는 도구: ${name}`)
  }
}

// ── 모델에 주입할 워크북 스냅샷 ───────────────────────────────────────────────

const PREVIEW_ROWS = 14
const PREVIEW_COLS = 12

export function workbookContext(): string {
  const st = useStore.getState()
  const active = st.activeSheet()
  const ev = getEvaluator()
  const lines: string[] = []

  lines.push(`파일: ${st.fileName}`)
  lines.push(
    '시트: ' +
      st.wb.sheets
        .map((s) => {
          const u = usedRange(s)
          const images = s.images?.length ? `, 이미지 ${s.images.length}개` : ''
          return `${s.name}${s.id === active.id ? '(활성)' : ''}[${u ? rangeToA1(u) : '비어있음'}${images}]`
        })
        .join(', ')
  )
  lines.push(`현재 선택: ${active.name}!${rangeToA1(st.selRange())}`)
  lines.push(`현재 리비전: ${st.version}`)

  const selected = st.selRange()
  const selectedCount = (selected.r2 - selected.r1 + 1) * (selected.c2 - selected.c1 + 1)
  if (selectedCount <= 60) {
    const selectedValues: string[] = []
    for (let row = selected.r1; row <= selected.r2; row++) {
      for (let col = selected.c1; col <= selected.c2; col++) {
        const cell = active.cells[ref(row, col)]
        const displayed = ev.display(active.name, row, col).text
        if (cell?.f || displayed)
          selectedValues.push(`${ref(row, col)}=${cell?.f ? `${displayed} (${cell.f})` : displayed}`)
      }
    }
    lines.push(`선택 영역 내용: ${selectedValues.length ? selectedValues.join(', ') : '비어있음'}`)
  } else {
    lines.push(`선택 영역 내용: ${selectedCount}셀 — 정확한 값이 필요하면 read_range 사용`)
  }

  const recent = st.recentChanges.slice(-12)
  if (recent.length) {
    lines.push('\n### 최근 변경 내역 (오래된 순)')
    for (const change of recent) {
      const who = change.actor === 'ai' ? 'AI' : '사용자'
      lines.push(`- r${change.revision} [${who}] ${change.label}: ${change.summary}`)
    }
  } else {
    lines.push('\n최근 변경 내역: 없음')
  }

  if (active.images?.length) {
    lines.push('\n### 활성 시트 이미지')
    for (const image of active.images.slice(0, 20))
      lines.push(`- ${image.name || image.id}: (${Math.round(image.x)}, ${Math.round(image.y)}) ${Math.round(image.width)}×${Math.round(image.height)}px`)
    lines.push('이미지의 실제 시각 내용은 채팅에 첨부된 경우에만 분석할 수 있습니다.')
  }

  const u = usedRange(active)
  if (!u) {
    lines.push(`\n활성 시트 "${active.name}" 는 비어 있습니다.`)
    return lines.join('\n')
  }

  const r2 = Math.min(u.r2, PREVIEW_ROWS)
  const c2 = Math.min(u.c2, PREVIEW_COLS)
  lines.push(`\n### ${active.name} 미리보기 (A1:${colName(c2)}${r2}, 계산된 값)`)
  const header = ['#', ...Array.from({ length: c2 }, (_, i) => colName(i + 1))]
  lines.push('| ' + header.join(' | ') + ' |')
  lines.push('|' + header.map(() => '---').join('|') + '|')
  for (let row = 1; row <= r2; row++) {
    const cells = [String(row)]
    for (let col = 1; col <= c2; col++) {
      const cell = active.cells[ref(row, col)]
      const txt = ev.display(active.name, row, col).text
      cells.push(cell?.f ? `${txt} \`${cell.f}\`` : txt.slice(0, 40))
    }
    lines.push('| ' + cells.join(' | ') + ' |')
  }
  if (u.r2 > r2 || u.c2 > c2)
    lines.push(`… 실제 사용범위는 ${rangeToA1(u)} 입니다. 전체가 필요하면 read_range 를 쓰세요.`)

  return lines.join('\n')
}

export const SYSTEM_PROMPT = `당신은 웹 스프레드시트 앱 "AI Sheet" 안에 내장된 조작 에이전트입니다.
사용자의 한국어(또는 영어) 요청을 받아 **도구를 직접 호출해서** 시트를 조작합니다.

원칙:
1. 추측하지 말고 먼저 확인한다. 데이터 구조가 불확실하면 read_range 로 읽고 나서 쓴다.
2. 여러 셀을 쓸 때는 write_cells 반복 대신 write_range 로 한 번에 쓴다.
3. 계산은 가능하면 직접 계산한 상수 대신 **수식**("=SUM(B2:B10)")으로 넣는다. 사용자가 나중에 값을 바꿔도 살아있게.
4. 파괴적인 작업(대량 삭제, 시트 삭제, 기존 데이터 덮어쓰기)은 실행 전에 무엇을 지울지 한 줄로 알리고, 사용자가 명확히 요청한 범위만 건드린다.
5. 작업이 끝나면 무엇을 어디에 했는지 **간결하게** 한국어로 요약한다. 도구 호출 내역을 그대로 나열하지 말 것.
6. 표를 새로 만들면 헤더 행은 bold + 배경색으로 서식을 주고, 숫자 열에는 적절한 num_format 을 적용한다.
7. 수식은 Excel 호환 함수 470여 종을 쓸 수 있다. SUM/AVERAGE/MAX/MIN/MEDIAN/STDEV/IF/IFS/IFERROR/
   VLOOKUP/HLOOKUP/XLOOKUP/INDEX/MATCH/COUNTIF(S)/SUMIF(S)/AVERAGEIF(S)/RANK/LARGE/SMALL/
   TEXT/SUBSTITUTE/TEXTJOIN/CONCATENATE/LEFT/RIGHT/MID/LEN/TRIM/SWITCH/CHOOSE/
   DATE/TODAY/NOW/PMT/NPV/IRR/FV/PV 등이 모두 동작한다.
   다만 **LAMBDA 와 동적배열(FILTER·UNIQUE·SORT 함수)은 지원하지 않는다** — 배열이 반환되면 좌상단 값만 남는다.
   그런 게 필요하면 값으로 직접 계산해서 채워 넣는다.
8. 결과를 강조하고 싶으면 마지막에 select_range 로 사용자 화면 선택을 그 범위에 맞춘다.
9. **병렬을 기본으로 삼는다.** 서로 독립적인 덩어리가 2개 이상이면 하나씩 처리하지 말고 run_parallel 로 동시에 돌린다.
   - 예: 시트 여러 개를 각각 정리 / 열 블록별 동일 변환 / 여러 구간을 각각 집계 / 조사와 작성 동시 진행
   - 각 작업의 쓰기 범위가 겹치지 않게 지시문에 대상 시트·범위를 못 박는다. 겹치면 서로 덮어쓴다.
   - 반대로 앞 결과가 있어야 다음을 할 수 있으면(의존) 병렬로 쪼개지 말 것.
10. 한 응답에서 여러 도구를 호출해도 된다. 읽기 도구는 동시에 실행된다.

매 턴 첫머리에 현재 워크북 스냅샷과 사용자/AI의 최근 변경 내역이 주어집니다.
사용자가 직접 바꾼 내용도 변경 내역에 표시됩니다. 스냅샷으로 충분하면 굳이 read_range 를 부르지 말고,
큰 범위의 정확한 현재 값이 필요할 때만 read_range 로 확인하세요. 이전 대화와 첨부에서 얻은 정보도
현재 통합문서 작업의 요구사항으로 이어서 사용하되, 원본에 없던 사실은 만들어내지 마세요.`

export const SUB_SYSTEM_PROMPT = `당신은 스프레드시트 조작 에이전트의 **병렬 하위 작업자**입니다.
아래 지시문 하나만 수행하고 끝냅니다.

- 지시문에 명시된 시트·범위 **밖은 절대 건드리지 않습니다** (다른 작업자가 동시에 일하고 있습니다).
- 모든 도구 호출에 **sheet 인자를 반드시 명시**합니다. 생략하면 다른 작업자가 바꾼 활성 시트로 잘못 갈 수 있습니다.
- run_parallel 은 쓸 수 없습니다. 필요한 도구를 직접 호출하세요.
- 사용자에게 말을 거는 게 아니라 상위 에이전트에 보고하는 것이므로, 마지막 응답은
  "무엇을 어디에 했는지" 1~2문장으로만 압축합니다. 인사·수식어 금지.
- 지시가 불가능하면 이유를 한 문장으로 반환합니다.`
